import PouchDB from 'pouchdb';
import PouchDBFind from 'pouchdb-find';

// Register the find plugin
PouchDB.plugin(PouchDBFind);

// Database names
const PUID_DB_NAME = 'meduler_puid_store';
const ANALYTICS_DB_NAME = 'meduler_analytics_events';
const USER_MAPPING_DB_NAME = 'meduler_user_mappings';

/**
 * PouchDB Service for Offline PUID and Analytics Support
 * 
 * Provides secure, offline-capable storage for pseudonymous user IDs
 * and queued analytics events when offline.
 */
class PouchDBService {
  private puidDB: PouchDB.Database;
  private analyticsDB: PouchDB.Database;
  private userMappingDB: PouchDB.Database;
  private initialized = false;

  constructor() {
    // Initialize databases lazily to ensure proper context
  }

  /**
   * Initialize the PouchDB databases
   * Should be called early in the application lifecycle
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Create databases with explicit adapter to avoid db.type() deprecation
      const dbOptions = { 
        auto_compaction: true,
        // Don't use adapter property directly as it still causes db.type() deprecation warnings
      };
      
      // Create databases without adapter parameter to avoid db.type() deprecation
      // We'll use the default adapter (IndexedDB in browsers, LevelDB in Node)
      this.puidDB = new PouchDB(PUID_DB_NAME, dbOptions);
      this.analyticsDB = new PouchDB(ANALYTICS_DB_NAME, dbOptions);
      this.userMappingDB = new PouchDB(USER_MAPPING_DB_NAME, dbOptions);
      
      // Verify database creation without using type() method
      await Promise.all([
        this.puidDB.info(),
        this.analyticsDB.info(),
        this.userMappingDB.info()
      ]);
      
      // Create indexes for efficient queries
      await this.createIndexes();
      
      this.initialized = true;
      // Successfully initialized
    } catch (error) {
      
    }
  }

  /**
   * Create database indexes for efficient queries
   * Updated to avoid using deprecated PouchDB methods
   */
  private async createIndexes(): Promise<void> {
    try {
      // Helper function to safely create an index without using deprecated methods
      const safeCreateIndex = async (db: PouchDB.Database, fields: string[]) => {
        try {
          await db.createIndex({
            index: { fields }
            // Note: the PouchDB typings don't support 'name' in createIndex options
            // We'll rely on other methods to avoid the deprecation warning
          });
        } catch (err: any) {
          // Log but don't throw to allow other indexes to be created
          // Silently continue if index creation fails
        }
      };

      // Create indexes in parallel but handle errors individually
      await Promise.allSettled([
        // Index for analytics events by timestamp
        safeCreateIndex(this.analyticsDB, ['timestamp']),
        
        // Index for user mappings
        safeCreateIndex(this.userMappingDB, ['userId', 'puid'])
      ]);
      
    } catch (error) {
      // Continue even if index creation fails
    }
  }

  /**
   * Store or update a PUID
   * 
   * @param puid The pseudonymous user ID to store
   * @param encryptedData Optional encrypted version for verification
   */
  public async storePUID(puid: string, encryptedData?: string): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      const timestamp = new Date().toISOString();
      
      // Check if we already have a record for this PUID
      const existingDoc = await this.getPUIDDoc();
      
      if (existingDoc) {
        // Update existing record
        await this.puidDB.put({
          _id: existingDoc._id,
          _rev: existingDoc._rev,
          puid,
          encryptedData,
          updated: timestamp,
          created: existingDoc.created
        });
      } else {
        // Create new record
        await this.puidDB.put({
          _id: 'current_puid',
          puid,
          encryptedData,
          created: timestamp,
          updated: timestamp
        });
      }
      

    } catch (error) {
      
    }
  }

  /**
   * Retrieve the current PUID
   * 
   * @returns The stored PUID or null if not found
   */
  public async retrievePUID(): Promise<string | null> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      const doc = await this.getPUIDDoc();
      return doc ? doc.puid : null;
    } catch (error) {
      
      return null;
    }
  }

  /**
   * Get the PUID document from the database
   */
  /**
   * Get the PUID document from the database
   * @returns The PUID document with proper typing or null if not found
   */
  private async getPUIDDoc(): Promise<{
    _id: string;
    _rev: string;
    puid: string;
    encryptedData?: string;
    created: string;
    updated: string;
  } | null> {
    try {
      const doc = await this.puidDB.get('current_puid');
      return doc as {
        _id: string;
        _rev: string;
        puid: string;
        encryptedData?: string;
        created: string;
        updated: string;
      };
    } catch (error: any) {
      if (error.name === 'not_found') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Store a mapping between a real user ID and a PUID
   * This is stored only locally for reference
   * 
   * @param userId The real user identifier
   * @param puid The pseudonymous user ID
   */
  public async storeUserMapping(userId: string, puid: string): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      const timestamp = new Date().toISOString();
      const id = `mapping_${userId}`;
      
      // Check if mapping already exists
      try {
        // Type casting to include the expected fields
        const existing = await this.userMappingDB.get(id) as {
          _id: string;
          _rev: string;
          userId: string;
          puid: string;
          created: string;
          updated: string;
        };
        
        // Update existing mapping
        await this.userMappingDB.put({
          _id: id,
          _rev: existing._rev,
          userId,
          puid,
          updated: timestamp,
          created: existing.created
        });
      } catch (error: any) {
        if (error.name === 'not_found') {
          // Create new mapping
          await this.userMappingDB.put({
            _id: id,
            userId,
            puid,
            created: timestamp,
            updated: timestamp
          });
        } else {
          throw error;
        }
      }
    } catch (error) {
      
    }
  }

  /**
   * Queue an analytics event for later sending
   * Useful when offline or when events need to be batched
   * 
   * @param eventName Name of the analytics event
   * @param properties Event properties
   * @param puid The pseudonymous user ID
   */
  public async queueAnalyticsEvent(
    eventName: string, 
    properties: Record<string, any>,
    puid: string
  ): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      const timestamp = new Date().toISOString();
      const id = `event_${timestamp}_${Math.random().toString(36).substring(2, 9)}`;
      
      await this.analyticsDB.put({
        _id: id,
        eventName,
        properties,
        puid,
        timestamp,
        sent: false
      });
    } catch (error) {
      
    }
  }

  /**
   * Retrieve unsent analytics events
   */
  public async getUnsentEvents(limit = 50): Promise<any[]> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      const result = await this.analyticsDB.find({
        selector: {
          sent: false
        },
        sort: [{ timestamp: 'asc' }],
        limit
      });
      
      return result.docs;
    } catch (error) {
      
      return [];
    }
  }

  /**
   * Mark analytics events as sent
   */
  public async markEventsSent(eventIds: string[]): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      // Get the current docs
      const docs = await Promise.all(
        eventIds.map(id => this.analyticsDB.get(id))
      );
      
      // Update them as sent
      const updates = docs.map(doc => ({
        ...doc,
        sent: true
      }));
      
      // Bulk update
      await this.analyticsDB.bulkDocs(updates);
    } catch (error) {
      
    }
  }

  /**
   * Clean up old events that have been sent
   * To prevent the database from growing too large
   */
  public async cleanupSentEvents(olderThanDays = 30): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
      const cutoffTimestamp = cutoffDate.toISOString();
      
      const result = await this.analyticsDB.find({
        selector: {
          sent: true,
          timestamp: { $lt: cutoffTimestamp }
        }
      });
      
      if (result.docs.length > 0) {
        const docsToDelete = result.docs.map(doc => ({
          _id: doc._id,
          _rev: doc._rev,
          _deleted: true
        }));
        
        await this.analyticsDB.bulkDocs(docsToDelete);

      }
    } catch (error) {
      
    }
  }

  /**
   * Destroy all databases (for testing or user data deletion)
   */
  public async destroyDatabases(): Promise<void> {
    try {
      if (this.puidDB) await this.puidDB.destroy();
      if (this.analyticsDB) await this.analyticsDB.destroy();
      if (this.userMappingDB) await this.userMappingDB.destroy();
      
      this.initialized = false;

    } catch (error) {
      
    }
  }
}

export const pouchDBService = new PouchDBService();
